import React, { useState, useEffect } from "react";
import {
  Building2,
  Sliders,
  Heart,
  Share2,
} from "lucide-react";
import Button from "../../components/common/Button";
import { MediaPickerModal } from "../../components/common/MediaPickerModal";
import { MediaAsset } from "../../services/mediaService";
import { useToast } from "../../hooks/otherHooks/useToast";
import { companyService } from "../../services/companyService";

import {
  CompanyInfoData,
  CoreValueItem,
  SocialUrlData,
} from "./components/companySettings/types";
import { CompanyInfoTab } from "./components/companySettings/CompanyInfoTab";
import { CoreValuesTab } from "./components/companySettings/CoreValuesTab";
import { SocialUrlsTab } from "./components/companySettings/SocialUrlsTab";
import { DeleteConfirmModal } from "./components/companySettings/DeleteConfirmModal";

export type { CompanyInfoData, CoreValueItem, SocialUrlData };

const SuperAdminCompanySettings: React.FC = () => {
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<"settings" | "core-values" | "social-urls">("settings");

  // 1. Company Settings State
  const [companyData, setCompanyData] = useState<CompanyInfoData | null>(null);
  const [companyInfoId, setCompanyInfoId] = useState<number | null>(null);
  const [loadingInfo, setLoadingInfo] = useState<boolean>(true);
  const [savingInfo, setSavingInfo] = useState<boolean>(false);
  const [isEditingInfo, setIsEditingInfo] = useState<boolean>(false);

  const [companyName, setCompanyName] = useState<string>("");
  const [companyAddress, setCompanyAddress] = useState<string>("");
  const [telephone, setTelephone] = useState<string>("");
  const [telephone2, setTelephone2] = useState<string>("");
  const [telephone3, setTelephone3] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [email2, setEmail2] = useState<string>("");
  const [email3, setEmail3] = useState<string>("");
  const [aboutCompany, setAboutCompany] = useState<string>("");
  const [ceoStatement, setCeoStatement] = useState<string>("");
  const [returnPolicy, setReturnPolicy] = useState<string>("");
  const [termsAndConditions, setTermsAndConditions] = useState<string>("");
  const [privacyPolicy, setPrivacyPolicy] = useState<string>("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoMediaId, setLogoMediaId] = useState<number | string | null>(null);

  // 2. Core Values State
  const [coreValuesList, setCoreValuesList] = useState<CoreValueItem[]>([]);
  const [loadingValues, setLoadingValues] = useState<boolean>(false);
  const [valueModalOpen, setValueModalOpen] = useState<boolean>(false);
  const [editingValueId, setEditingValueId] = useState<number | null>(null);
  const [valueTitle, setValueTitle] = useState<string>("");
  const [valueDescription, setValueDescription] = useState<string>("");
  const [valuePicFile, setValuePicFile] = useState<File | null>(null);
  const [valuePicUrl, setValuePicUrl] = useState<string>("");
  const [valuePicMediaId, setValuePicMediaId] = useState<number | string | null>(null);
  const [savingValue, setSavingValue] = useState<boolean>(false);

  // 3. Social URLs State
  const [socialData, setSocialData] = useState<SocialUrlData | null>(null);
  const [socialId, setSocialId] = useState<number | null>(null);
  const [loadingSocial, setLoadingSocial] = useState<boolean>(false);
  const [savingSocial, setSavingSocial] = useState<boolean>(false);
  const [isEditingSocial, setIsEditingSocial] = useState<boolean>(false);
  const [facebookUrl, setFacebookUrl] = useState<string>("");
  const [instagramUrl, setInstagramUrl] = useState<string>("");
  const [twitterUrl, setTwitterUrl] = useState<string>("");
  const [linkedinUrl, setLinkedinUrl] = useState<string>("");
  const [githubUrl, setGithubUrl] = useState<string>("");
  const [youtubeUrl, setYoutubeUrl] = useState<string>("");
  const [whatsappUrl, setWhatsappUrl] = useState<string>("");

  // Media Library Selection State
  const [isMediaModalOpen, setIsMediaModalOpen] = useState<boolean>(false);
  const [mediaTarget, setMediaTarget] = useState<"logo" | "core-value" | null>(null);

  // Delete Confirmation Modal State
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "core-value";
    id: number;
    name?: string;
  } | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  // Media Library Selection Handler
  const handleMediaSelect = (asset: MediaAsset) => {
    const fileUrl = asset.url || asset.file || "";
    if (mediaTarget === "logo") {
      setLogoPreview(fileUrl);
      setLogoFile(null);
      setLogoMediaId(asset.id);
    } else if (mediaTarget === "core-value") {
      setValuePicUrl(fileUrl);
      setValuePicFile(null);
      setValuePicMediaId(asset.id);
    }
    setIsMediaModalOpen(false);
    setMediaTarget(null);
  };

  // Open Modal for New Core Value
  const handleOpenAddValue = () => {
    setEditingValueId(null);
    setValueTitle("");
    setValueDescription("");
    setValuePicFile(null);
    setValuePicUrl("");
    setValuePicMediaId(null);
    setValueModalOpen(true);
  };

  // Open Modal for Edit Core Value
  const handleOpenEditValue = (item: CoreValueItem) => {
    setEditingValueId(item.id);
    setValueTitle(item.title || "");
    setValueDescription(item.description || "");
    setValuePicFile(null);
    setValuePicUrl(item.pic || item.pic_url || (item as any).picture || "");
    setValuePicMediaId(item.pic_media_id || item.pic_media?.id || item.image_media_id || item.image_media?.id || null);
    setValueModalOpen(true);
  };

  // Populate Form Fields
  const populateFormFields = (info: CompanyInfoData) => {
    setCompanyData(info);
    setCompanyInfoId(info.id || null);
    setCompanyName(info.company_name || "");
    setCompanyAddress(info.company_address || "");
    setTelephone(info.telephone || "");
    setTelephone2(info.telephone_2 || "");
    setTelephone3(info.telephone_3 || "");
    setEmail(info.email || "");
    setEmail2(info.email_2 || "");
    setEmail3(info.email_3 || "");
    setAboutCompany(info.about_company || "");
    setCeoStatement(info.ceo_statment || "");
    setReturnPolicy(info.return_policy || "");
    setTermsAndConditions(info.term_and_conditions || "");
    setPrivacyPolicy(info.privacy_policy || "");
    setLogoPreview(info.logo_url || info.get_logo || info.logo || null);
    setLogoFile(null);
  };

  // Fetch Company Info
  const fetchCompanyInfo = async () => {
    setLoadingInfo(true);
    try {
      const data = await companyService.getCompanyInfo();
      if (data) {
        populateFormFields(data);
      } else {
        setIsEditingInfo(true);
      }
    } catch (err) {
      console.error("Error loading company info:", err);
      setIsEditingInfo(true);
    } finally {
      setLoadingInfo(false);
    }
  };

  // Fetch Core Values
  const fetchCoreValues = async () => {
    setLoadingValues(true);
    try {
      const list = await companyService.getCoreValues();
      setCoreValuesList(list);
    } catch (err) {
      console.error("Error fetching core values:", err);
    } finally {
      setLoadingValues(false);
    }
  };

  // Fetch Social URLs
  const fetchSocialUrls = async () => {
    setLoadingSocial(true);
    try {
      const soc = await companyService.getSocialUrls();
      if (soc) {
        setSocialData(soc);
        setSocialId(soc.id || null);
        setFacebookUrl(soc.facebook_url || "");
        setInstagramUrl(soc.instagram_url || "");
        setTwitterUrl(soc.twitter_url || "");
        setLinkedinUrl(soc.linkedin_url || "");
        setGithubUrl(soc.github_url || "");
        setYoutubeUrl(soc.youtube_url || "");
        setWhatsappUrl(soc.whatsapp_url || "");
      } else {
        setIsEditingSocial(true);
      }
    } catch (err) {
      console.error("Error fetching social URLs:", err);
    } finally {
      setLoadingSocial(false);
    }
  };

  useEffect(() => {
    fetchCompanyInfo();
    fetchCoreValues();
    fetchSocialUrls();
  }, []);

  // Save Company Info Form
  const handleSaveCompanyInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingInfo(true);
    try {
      const formData = new FormData();
      formData.append("company_name", companyName);
      formData.append("company_address", companyAddress);
      formData.append("telephone", telephone);
      formData.append("telephone_2", telephone2);
      formData.append("telephone_3", telephone3);
      formData.append("email", email);
      formData.append("email_2", email2);
      formData.append("email_3", email3);
      formData.append("about_company", aboutCompany);
      formData.append("ceo_statment", ceoStatement);
      formData.append("return_policy", returnPolicy);
      formData.append("term_and_conditions", termsAndConditions);
      formData.append("privacy_policy", privacyPolicy);

      if (logoFile) {
        formData.append("logo", logoFile);
      } else if (logoMediaId !== null && logoMediaId !== undefined) {
        formData.append("logo_media_id", String(logoMediaId));
      } else if (logoPreview) {
        formData.append("logo_url", logoPreview);
      }

      const updated = await companyService.saveCompanyInfo(formData, companyInfoId || undefined);
      populateFormFields(updated);
      setIsEditingInfo(false);
      addToast({
        message: companyInfoId
          ? "Company profile updated successfully!"
          : "Company profile created successfully!",
        bg: "success",
      });
    } catch (err) {
      console.error("Error saving company info:", err);
      addToast({ message: "Failed to save company settings.", bg: "danger" });
    } finally {
      setSavingInfo(false);
    }
  };

  // Save Social URLs Form
  const handleSaveSocialUrls = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSocial(true);
    try {
      const payload: Partial<SocialUrlData> = {
        facebook_url: facebookUrl,
        instagram_url: instagramUrl,
        twitter_url: twitterUrl,
        linkedin_url: linkedinUrl,
        github_url: githubUrl,
        youtube_url: youtubeUrl,
        whatsapp_url: whatsappUrl,
      };

      const updated = await companyService.saveSocialUrls(payload, socialId || undefined);
      setSocialData(updated);
      if (updated?.id) setSocialId(updated.id);
      setIsEditingSocial(false);
      addToast({ message: "Social media links updated successfully!", bg: "success" });
    } catch (err) {
      console.error("Error saving social URLs:", err);
      addToast({ message: "Failed to save social URLs.", bg: "danger" });
    } finally {
      setSavingSocial(false);
    }
  };

  // Save Core Value
  const handleSaveCoreValue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valueTitle.trim()) return;
    setSavingValue(true);
    try {
      const formData = new FormData();
      formData.append("title", valueTitle);
      formData.append("description", valueDescription);

      if (valuePicFile) {
        formData.append("pic", valuePicFile);
      } else if (valuePicMediaId !== null && valuePicMediaId !== undefined) {
        formData.append("pic_media_id", String(valuePicMediaId));
        formData.append("image_media_id", String(valuePicMediaId));
      } else if (valuePicUrl) {
        formData.append("pic_url", valuePicUrl);
      }

      if (editingValueId) {
        await companyService.updateCoreValue(editingValueId, formData);
        addToast({ message: "Core value updated successfully!", bg: "success" });
      } else {
        await companyService.createCoreValue(formData);
        addToast({ message: "New core value created successfully!", bg: "success" });
      }

      setValueModalOpen(false);
      setEditingValueId(null);
      setValueTitle("");
      setValueDescription("");
      setValuePicFile(null);
      setValuePicUrl("");
      setValuePicMediaId(null);
      fetchCoreValues();
    } catch (err) {
      console.error("Error saving core value:", err);
      addToast({ message: "Failed to save core value.", bg: "danger" });
    } finally {
      setSavingValue(false);
    }
  };

  // Delete Core Value Trigger
  const handleDeleteCoreValue = (id: number, name?: string) => {
    setDeleteTarget({ type: "core-value", id, name });
  };

  // Confirm Delete Action
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.type === "core-value") {
        await companyService.deleteCoreValue(deleteTarget.id);
        addToast({ message: "Core value deleted successfully!", bg: "success" });
        fetchCoreValues();
      }
    } catch (err) {
      console.error("Error deleting item:", err);
      addToast({ message: `Failed to delete ${deleteTarget.type}.`, bg: "danger" });
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  // 3-Tab UI Configuration
  const tabs = [
    {
      id: "settings",
      label: "Company Profile",
      icon: Sliders,
      badge: companyData ? "Configured" : "Action Needed",
    },
    {
      id: "core-values",
      label: "Core Values",
      icon: Heart,
      count: coreValuesList.length,
    },
    {
      id: "social-urls",
      label: "Social Links",
      icon: Share2,
      badge: socialData?.id ? "Active" : "Not Set",
    },
  ];

  return (
    <div className="cht-space-y-6 cht-max-w-7xl cht-mx-auto cht-pb-12">
      {/* Top Header */}
      <div className="cht-flex cht-flex-col md:cht-flex-row md:cht-items-center cht-justify-between cht-gap-4 cht-border-b cht-border-gray-200/80 cht-pb-5">
        <div>
          <div className="cht-flex cht-items-center cht-space-x-2">
            <span className="cht-bg-indigo-50 cht-text-[#3e57da] cht-text-[10px] cht-font-black cht-px-2.5 cht-py-0.5 cht-rounded-full cht-uppercase cht-tracking-wider">
              Executive Portal
            </span>
            <span className="cht-text-gray-300 cht-text-xs">•</span>
            <span className="cht-text-xs cht-text-gray-500 cht-font-semibold">Codehub Technologies LTD</span>
          </div>
          <h1 className="cht-text-2xl md:cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight cht-mt-1">
            Company Settings & Management
          </h1>
          <p className="cht-text-xs cht-text-gray-500 cht-font-medium cht-mt-1">
            Configure corporate profile, headquarters address, legal policies, core values, and public social connections.
          </p>
        </div>
      </div>

      {/* Navigation Tabs Bar */}
      <div className="cht-border-b cht-border-gray-200/80 cht-pb-2">
        <nav className="cht-flex cht-space-x-1 sm:cht-space-x-2 cht-overflow-x-auto" aria-label="Tabs">
          {tabs.map((t) => {
            const IconComponent = t.icon;
            const isActive = activeTab === t.id;
            return (
              <Button
                key={t.id}
                type="button"
                variant={isActive ? "primary" : "outline"}
                onClick={() => setActiveTab(t.id as any)}
                leftIcon={<IconComponent className="cht-h-4 cht-w-4 cht-shrink-0" />}
                className="cht-rounded-xl cht-font-bold cht-text-xs cht-shrink-0"
              >
                <span>{t.label}</span>
                {t.count !== undefined && (
                  <span
                    className={`cht-ml-1.5 cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black ${
                      isActive
                        ? "cht-bg-white/20 cht-text-white"
                        : "cht-bg-indigo-50 cht-text-[#3e57da]"
                    }`}
                  >
                    {t.count}
                  </span>
                )}
                {t.badge && (
                  <span
                    className={`cht-ml-1.5 cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black ${
                      isActive
                        ? "cht-bg-white/20 cht-text-white"
                        : "cht-bg-gray-100 cht-text-gray-500"
                    }`}
                  >
                    {t.badge}
                  </span>
                )}
              </Button>
            );
          })}
        </nav>
      </div>

      {/* TAB CONTENTS */}
      {activeTab === "settings" && (
        <CompanyInfoTab
          companyData={companyData}
          loadingInfo={loadingInfo}
          savingInfo={savingInfo}
          isEditingInfo={isEditingInfo}
          setIsEditingInfo={setIsEditingInfo}
          companyName={companyName}
          setCompanyName={setCompanyName}
          companyAddress={companyAddress}
          setCompanyAddress={setCompanyAddress}
          telephone={telephone}
          setTelephone={setTelephone}
          telephone2={telephone2}
          setTelephone2={setTelephone2}
          telephone3={telephone3}
          setTelephone3={setTelephone3}
          email={email}
          setEmail={setEmail}
          email2={email2}
          setEmail2={setEmail2}
          email3={email3}
          setEmail3={setEmail3}
          aboutCompany={aboutCompany}
          setAboutCompany={setAboutCompany}
          ceoStatement={ceoStatement}
          setCeoStatement={setCeoStatement}
          returnPolicy={returnPolicy}
          setReturnPolicy={setReturnPolicy}
          termsAndConditions={termsAndConditions}
          setTermsAndConditions={setTermsAndConditions}
          privacyPolicy={privacyPolicy}
          setPrivacyPolicy={setPrivacyPolicy}
          logoFile={logoFile}
          setLogoFile={setLogoFile}
          logoPreview={logoPreview}
          setLogoPreview={setLogoPreview}
          handleSaveCompanyInfo={handleSaveCompanyInfo}
          onOpenMediaModalForLogo={() => {
            setMediaTarget("logo");
            setIsMediaModalOpen(true);
          }}
        />
      )}

      {activeTab === "core-values" && (
        <CoreValuesTab
          coreValuesList={coreValuesList}
          loadingValues={loadingValues}
          valueModalOpen={valueModalOpen}
          setValueModalOpen={setValueModalOpen}
          editingValueId={editingValueId}
          valueTitle={valueTitle}
          setValueTitle={setValueTitle}
          valueDescription={valueDescription}
          setValueDescription={setValueDescription}
          valuePicFile={valuePicFile}
          setValuePicFile={setValuePicFile}
          valuePicUrl={valuePicUrl}
          setValuePicUrl={setValuePicUrl}
          setValuePicMediaId={setValuePicMediaId}
          savingValue={savingValue}
          handleOpenAddValue={handleOpenAddValue}
          handleOpenEditValue={handleOpenEditValue}
          handleSaveCoreValue={handleSaveCoreValue}
          handleDeleteCoreValue={handleDeleteCoreValue}
          onOpenMediaModalForCoreValue={() => {
            setMediaTarget("core-value");
            setIsMediaModalOpen(true);
          }}
        />
      )}

      {activeTab === "social-urls" && (
        <SocialUrlsTab
          socialData={socialData}
          loadingSocial={loadingSocial}
          savingSocial={savingSocial}
          isEditingSocial={isEditingSocial}
          setIsEditingSocial={setIsEditingSocial}
          facebookUrl={facebookUrl}
          setFacebookUrl={setFacebookUrl}
          instagramUrl={instagramUrl}
          setInstagramUrl={setInstagramUrl}
          twitterUrl={twitterUrl}
          setTwitterUrl={setTwitterUrl}
          linkedinUrl={linkedinUrl}
          setLinkedinUrl={setLinkedinUrl}
          githubUrl={githubUrl}
          setGithubUrl={setGithubUrl}
          youtubeUrl={youtubeUrl}
          setYoutubeUrl={setYoutubeUrl}
          whatsappUrl={whatsappUrl}
          setWhatsappUrl={setWhatsappUrl}
          handleSaveSocialUrls={handleSaveSocialUrls}
        />
      )}

      {/* REUSABLE MEDIA LIBRARY MODAL */}
      <MediaPickerModal
        isOpen={isMediaModalOpen}
        onClose={() => {
          setIsMediaModalOpen(false);
          setMediaTarget(null);
        }}
        onSelect={handleMediaSelect}
        title={
          mediaTarget === "logo"
            ? "Select Brand Logo from Media Library"
            : "Select Core Value Icon from Media Library"
        }
        allowedTypes={["image"]}
      />

      {/* REUSABLE DELETE CONFIRMATION MODAL */}
      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        itemName={deleteTarget?.name}
        itemType={deleteTarget?.type}
        loading={deleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default SuperAdminCompanySettings;
